import { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../utils/api';

export interface PendingCounts {
  mrPending: number;
  mrTotal: number;
  poPending: number;
  poTotal: number;
  contractPending: number;
  contractTotal: number;
  voPending: number;
  voTotal: number;
  dcPending: number;
  dcTotal: number;
  siteMrTotal: number;
  qsPoPending: number;
  qsPoTotal: number;
  qsContractPending: number;
  qsContractTotal: number;
  qsVoPending: number;
  qsVoTotal: number;
  qsDcPending: number;
  qsDcTotal: number;
}

const defaultCounts: PendingCounts = {
  mrPending: 0, mrTotal: 0,
  poPending: 0, poTotal: 0,
  contractPending: 0, contractTotal: 0,
  voPending: 0, voTotal: 0,
  dcPending: 0, dcTotal: 0,
  siteMrTotal: 0,
  qsPoPending: 0, qsPoTotal: 0,
  qsContractPending: 0, qsContractTotal: 0,
  qsVoPending: 0, qsVoTotal: 0,
  qsDcPending: 0, qsDcTotal: 0,
};

export const usePendingCounts = (projectId: number | null) => {
  const [counts, setCounts] = useState<PendingCounts>(defaultCounts);

  const fetchCounts = useCallback(() => {
    if (!projectId) {
      setCounts(defaultCounts);
      return;
    }

    fetch(apiUrl(`/api/material-requisitions?project_id=${projectId}`), { credentials: 'include' })
      .then(r => r.json())
      .then(rows => {
        if (Array.isArray(rows)) {
          const relevant = rows.filter(mr => mr.status === 'Pending Approval' || mr.status === 'Approved');
          setCounts(prev => ({
            ...prev,
            mrPending: relevant.filter(mr => mr.status === 'Pending Approval').length,
            mrTotal: relevant.length,
            siteMrTotal: rows.length,
          }));
        }
      })
      .catch(() => {});

    fetch(apiUrl(`/api/purchase-orders?project_id=${projectId}`), { credentials: 'include' })
      .then(r => r.json())
      .then(rows => {
        if (Array.isArray(rows)) {
          const relevant = rows.filter(po => po.status === 'Pending Approval' || po.status === 'Approved');
          setCounts(prev => ({
            ...prev,
            poPending: relevant.filter(po => po.status === 'Pending Approval').length,
            poTotal: relevant.length,
            qsPoPending: rows.filter(po => po.status === 'Pending Approval').length,
            qsPoTotal: rows.length,
          }));
        }
      })
      .catch(() => {});

    fetch(apiUrl(`/api/contracts?project_id=${projectId}`), { credentials: 'include' })
      .then(r => r.json())
      .then(rows => {
        if (Array.isArray(rows)) {
          const relevant = rows.filter(c => c.status === 'Pending Approval' || c.status === 'Approved');
          const contracts = relevant.filter(c => c.contract_type !== 'variation_order');
          const vos = relevant.filter(c => c.contract_type === 'variation_order');
          const allContracts = rows.filter(c => c.contract_type !== 'variation_order');
          const allVos = rows.filter(c => c.contract_type === 'variation_order');
          setCounts(prev => ({
            ...prev,
            contractPending: contracts.filter(c => c.status === 'Pending Approval').length,
            contractTotal: contracts.length,
            voPending: vos.filter(c => c.status === 'Pending Approval').length,
            voTotal: vos.length,
            qsContractPending: allContracts.filter(c => c.status === 'Pending Approval').length,
            qsContractTotal: allContracts.length,
            qsVoPending: allVos.filter(c => c.status === 'Pending Approval').length,
            qsVoTotal: allVos.length,
          }));
        }
      })
      .catch(() => {});

    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`), { credentials: 'include' })
      .then(r => r.json())
      .then(rows => {
        if (Array.isArray(rows)) {
          const qsRelevant = rows.filter(dc => dc.qs_status === 'Pending' || dc.qs_status === 'Approved');
          setCounts(prev => ({
            ...prev,
            dcPending: rows.filter(dc => dc.change_status === 'Pending').length,
            dcTotal: rows.length,
            qsDcPending: qsRelevant.filter(dc => dc.qs_status !== 'Approved').length,
            qsDcTotal: qsRelevant.length,
          }));
        }
      })
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    const handler = () => fetchCounts();
    window.addEventListener('refresh-pending-counts', handler);
    return () => window.removeEventListener('refresh-pending-counts', handler);
  }, [fetchCounts]);

  return { counts, refreshCounts: fetchCounts };
};
